import { ActivityType } from '@omni/classes/activity/activity.class';

/** Calendar activity colour model. See docs/calendar-activity-colors.md. */

/** Config keys, as an admin types them. Not the raw `ActivityType` enum, whose values
 *  are not admin-safe (`followuptask`, `Joint Meeting` with a space). */
export enum CalendarActivityType {
  Meeting = 'Meeting',
  StoreCheck = 'StoreCheck',
  PhoneCall = 'PhoneCall',
  Message = 'Message',
  TimeOff = 'TimeOff',
  AllocationOrder = 'AllocationOrder',
  FollowUp = 'FollowUp',
  Order = 'Order',
  CustomerInquiry = 'CustomerInquiry',
  MedicalInquiry = 'MedicalInquiry',
  ProcedureLog = 'ProcedureLog',
  ProcedureTracker = 'ProcedureTracker',
  KitBooking = 'KitBooking',
}

/** Discriminated `ActivityType` -> config key. Appointment and phone-call variants fold
 *  into their family key, so a joint remote meeting has no precedence question. */
export const ACTIVITY_TYPE_TO_CONFIG_KEY: ReadonlyMap<ActivityType, CalendarActivityType> = new Map([
  [ActivityType.Appointment, CalendarActivityType.Meeting],
  [ActivityType.LiveMeet, CalendarActivityType.Meeting],
  [ActivityType.JointMeeting, CalendarActivityType.Meeting],
  [ActivityType.RemoteMeeting, CalendarActivityType.Meeting],
  [ActivityType.StoreCheck, CalendarActivityType.StoreCheck],
  [ActivityType.PhoneCall, CalendarActivityType.PhoneCall],
  [ActivityType.JointPhoneCall, CalendarActivityType.PhoneCall],
  [ActivityType.Email, CalendarActivityType.Message],
  [ActivityType.TimeOff, CalendarActivityType.TimeOff],
  [ActivityType.TimeOffRequest, CalendarActivityType.TimeOff],
  [ActivityType.Sample, CalendarActivityType.AllocationOrder],
  [ActivityType.FollowUp, CalendarActivityType.FollowUp],
  [ActivityType.Order, CalendarActivityType.Order],
  [ActivityType.CaseIntake, CalendarActivityType.CustomerInquiry],
  [ActivityType.MedicalInquiry, CalendarActivityType.MedicalInquiry],
  [ActivityType.SurgeryOrder, CalendarActivityType.ProcedureLog],
  [ActivityType.ProcedureTracker, CalendarActivityType.ProcedureTracker],
  [ActivityType.SetBooking, CalendarActivityType.KitBooking],
]);

/**
 * The shipped palette, used when nothing is configured. Two constraints, both asserted
 * by tests: every colour is light enough that its ink derives DOWNWARD, and unrelated
 * types stay 0.10+ apart in OKLab. Lightness varies on purpose - read the doc before
 * flattening it. Meeting, TimeOff and ProcedureLog carry the legacy #c2e6ff / #bf9aad /
 * #2bb673 forward.
 */
export const DEFAULT_PALETTE: Readonly<Record<CalendarActivityType, string>> = {
  [CalendarActivityType.Meeting]:          '#c2e6ff',
  [CalendarActivityType.PhoneCall]:        '#93E5FA',
  [CalendarActivityType.Message]:          '#ACC0FF',
  [CalendarActivityType.CustomerInquiry]:  '#BF94F9',
  [CalendarActivityType.MedicalInquiry]:   '#D488E4',
  [CalendarActivityType.TimeOff]:          '#C795A6',
  [CalendarActivityType.FollowUp]:         '#E06B55',
  [CalendarActivityType.AllocationOrder]:  '#F5B87A',
  [CalendarActivityType.Order]:            '#E8C23F',
  [CalendarActivityType.StoreCheck]:       '#AD9A34',
  [CalendarActivityType.ProcedureTracker]: '#75C86E',
  [CalendarActivityType.ProcedureLog]:     '#62D099',
  [CalendarActivityType.KitBooking]:       '#54CAC1',
};

/** Base for an activity type added after this file was written. */
export const FALLBACK_BASE = DEFAULT_PALETTE[CalendarActivityType.Meeting];

export interface ResolvedChipStyle {
  readonly fill: string;
  /** For anything drawn ON the fill: chip title and second line, accent bar, popover
   *  avatar glyph and pill text. Deliberately LIGHT for a dark fill. */
  readonly ink: string;
  /** For anything on the popover's white card - its title, banner, and the chip's
   *  selection ring. Light fill -> the same `ink`; dark fill -> the fill itself. */
  readonly inkOnCard: string;
}

/** One activity's entry. `base` is required; every other key is a status name from
 *  CALENDAR_STATUS_KEYS, which differs per type - hence the index signature. */
export interface CalendarActivityColorEntry {
  base?: string;
  [statusKey: string]: string | undefined;
}

/** The `indskr_configvalue` payload: a flat map of activity key -> entry, e.g.
 *  `{ "Meeting": { "base": "#2374B6", "completed": "#032c4d" } }`. An optional
 *  `schemaVersion` may sit alongside the keys and is skipped as a non-activity. */
export type CalendarColorConfig = {
  schemaVersion?: number;
  /** Records written against the first draft nested everything here. Still accepted. */
  activities?: Record<string, CalendarActivityColorEntry>;
} & {
  [activityKey: string]: CalendarActivityColorEntry | number | Record<string, CalendarActivityColorEntry> | undefined;
};

/** Highest config schema this build understands. Absent in a record means 1. */
export const SUPPORTED_SCHEMA_VERSION = 1;

export const BASE_KEY = 'base';
